import { Card, MenuItem, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';
import AllderTable from '../../../components/AllderTable';
import { useGlobalTable } from '../../../hooks/useGlobalTable';
import { customerHandler, masterHandler } from '../../../redux/handlers';
import { createTableColumns } from '../../../utils/createTableColumns';

const TYPE_ACTION_OPTIONS = [
  { value: 'ALL', label: 'ทั้งหมด' },
  { value: 'CREATE', label: 'สร้าง' },
  { value: 'UPDATE', label: 'แก้ไข' },
  { value: 'DELETE', label: 'ลบ' }
];

const TYPE_MAP = {
  CREATE: 'สร้าง',
  UPDATE: 'แก้ไข',
  DELETE: 'ลบ'
};

const TEXTFIELD_STYLES = {
  InputProps: { sx: { borderRadius: '7px' } },
  sx: {
    '.Mui-disabled': {
      background: '#F8F8F9'
    }
  },
  className: 'w-[200px] !ml',
  size: 'small'
};

export function CustomerHistoryTab() {
  const dispatch = useDispatch();
  const { customerID } = useParams();
  const [businessGroupId, setBusinessGroupId] = useState('');
  const [actionType, setActionType] = useState('ALL');

  const businessCustomerGroups = useSelector(state => state.master.businessCustomerGroups);

  const fetchHistoryData = useCallback(
    params => {
      const queryParams = {
        ...params,
        ...(businessGroupId && { business_group_id: Number(businessGroupId) }),
        ...(actionType !== 'ALL' && { type: actionType })
      };
      return dispatch(customerHandler.getUserBusinessGroupLogs(queryParams));
    },
    [dispatch, businessGroupId, actionType]
  );

  const {
    data: historyData,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort
  } = useGlobalTable({
    fetchData: fetchHistoryData,
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultPageSize: 20
  });

  useEffect(() => {
    dispatch(masterHandler.getBusinessCustomerGroups());
  }, [dispatch]);

  useEffect(() => {
    const fetchCustomerData = async () => {
      const customerInfo = await dispatch(customerHandler.getCustomerById(customerID));
      if (customerInfo) {
        setBusinessGroupId(customerInfo.business_group_id?.toString() || '');
      } else if (businessCustomerGroups?.length > 0) {
        setBusinessGroupId(businessCustomerGroups[0].id.toString());
      }
    };
    fetchCustomerData();
  }, [dispatch, customerID, businessCustomerGroups]);

  const renderUserInfo = row => {
    const isBO = row.user_business_group_edit_id;
    const user = isBO ? row.userBusinessGroupEdit : row.userAdmin;

    if (!user) return '-';

    return (
      <div className="d-flex align-items-center">
        <span>{`${user.prefixName?.name || ''} ${user.first_name || ''} ${user.last_name || ''}`}</span>
        <span className="ml-1" style={{ color: isBO ? '#2192FF' : '#2DC76D' }}>
          {isBO ? '(BO)' : '(SA)'}
        </span>
      </div>
    );
  };

  const columns = createTableColumns([
    {
      field: 'businessGroup',
      headerName: 'รหัสธุรกิจ',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => <div>{row.business?.code || 'ACCOUNT'}</div>
    },
    {
      field: 'created_at',
      headerName: 'วัน-เวลา',
      width: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <div className="text-[#2DC76D]">{dayjs(row.created_at).format('DD/MM/YYYY HH:mm')} น.</div>
      )
    },
    {
      field: 'type',
      headerName: 'ประเภท',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => <div>{TYPE_MAP[row.type] || row.type}</div>
    },
    {
      field: 'notes',
      headerName: 'รายละเอียด',
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => <div dangerouslySetInnerHTML={{ __html: row.notes }} />
    },
    {
      field: 'userAdmin',
      headerName: 'ผู้ดำเนินการ',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => renderUserInfo(row)
    }
  ]);

  return (
    <Card className="!rounded-lg mb-4">
      <div className="py-4">
        <div className="d-flex align-items-center px-4 mb-4">
          <TextField
            {...TEXTFIELD_STYLES}
            select
            label="เลือกดูตามธุระกิจ"
            value={businessGroupId}
            onChange={e => setBusinessGroupId(e.target.value)}
          >
            {(businessCustomerGroups || []).map(group => (
              <MenuItem key={group.id} value={group.id.toString()}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            {...TEXTFIELD_STYLES}
            select
            label="เลือกดูตามประเภท"
            value={actionType}
            onChange={e => setActionType(e.target.value)}
            className="me-auto w-[200px] !ml-7"
          >
            {TYPE_ACTION_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <AllderTable
          data={historyData}
          loading={loading}
          columns={columns}
          totalRows={totalRows}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          handleSort={handleSort}
        />
      </div>
    </Card>
  );
}
